import { FC, useMemo } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { DefaultEventCategoryId, TaskItem } from '@demind-inc/core';
import { Button, Stack, Typography } from '@mui/material';
import { BorderColor } from '@mui/icons-material';

import './EventBlock.scss';
import { RBCEvent } from '../types';
import { getCssVariable } from '../../../../utils/styles/styles';
import {
  getDefaultCategoryEventIconSet,
  TaskCheckBox,
  LinearGradientText,
  EventMetrics,
} from '../../../common';
import { isAppleRemindersDesktopSupported } from '../../../../helpers/appleRemindersDesktop.helpers';
import {
  isAISuggestedEvent,
  isTemporalAiSuggestedTaskEvent,
  isTemporalAutoScheduledEvent,
} from '../../../../helpers';
import { useGeneralSettings } from '../../../../hooks';
import { EventImageBgWrapper } from '../EventImageBgWrapper';

import SparkleIcon from '../../../../assets/scheduler/ai_scheduler_logo.png';
import { isFutureDate, isMealSuggestingAtom } from '../../../../data-access';
import SuggestedTaskActions from './SuggestedTaskActions';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

interface EventBlockProps {
  event: Partial<RBCEvent>;
  className?: string;
  short?: boolean;
  monthView?: boolean;
  /** Dense week grid: static suggested chrome (no infinite glow). */
  weekView?: boolean;
  taskLinkedToEvent?: TaskItem;
  isMealSuggesting?: boolean;
  onTaskStatusChange?: (
    taskId: string,
    boardId: string,
    completed: boolean,
    originalTaskId?: string
  ) => void;
  onRoutineStatusChange?: (event: RBCEvent, completed: boolean) => void;
  onDiscardSuggestedTask?: (eventId: string) => void;
  onAcceptSuggestedTask?: (event: Partial<RBCEvent>, calendarId: string) => void;
}

const primaryColor = getCssVariable('--color-primary') || '#680ddb';
const blackColor = getCssVariable('--color-black') || '#131314';

const EventBlock: FC<EventBlockProps> = ({
  event,
  className,
  short = false,
  monthView = false,
  taskLinkedToEvent,
  onTaskStatusChange,
  onRoutineStatusChange,
  onDiscardSuggestedTask,
  onAcceptSuggestedTask,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const isMealSuggesting = useRecoilValue(isMealSuggestingAtom);
  const accentColor = event.color || event.calendarColor || primaryColor;
  const isReplanPreview = event.isReplanPreview === true;
  const { isShortEvent, isShortestEvent, isFiveMinuteEvent } = useMemo(() => {
    const durationMinutes = Math.abs(dayjs(event.end).diff(dayjs(event.start), 'minutes'));
    const isShortEvent = short || durationMinutes <= 30;
    const isShortestEvent = durationMinutes <= 15;
    const isFiveMinuteEvent = durationMinutes <= 5;
    return { isShortEvent, isShortestEvent, isFiveMinuteEvent };
  }, [event, short]);

  const { isAiSuggetedEvent, isAiSuggetedTemporalTaskEvent, isTemporalAiSchedulerEvent } =
    useMemo(() => {
      const isAiSuggetedEvent = isAISuggestedEvent(event.autoScheduleFrom);
      const isAiSuggetedTemporalTaskEvent = isTemporalAiSuggestedTaskEvent(event);
      const isTemporalAiSchedulerEvent = isTemporalAutoScheduledEvent(event.eventId!, [
        'aiScheduler',
      ]);

      return {
        isAiSuggetedEvent,
        isAiSuggetedTemporalTaskEvent,
        isTemporalAiSchedulerEvent,
      };
    }, [event.autoScheduleFrom, event.eventId]);

  const showSuggestedIndicator =
    isReplanPreview ||
    event.isTemporalSuggested ||
    isAiSuggetedTemporalTaskEvent ||
    isTemporalAiSchedulerEvent ||
    (event.autoScheduleFrom?.from === 'meal' &&
      isMealSuggesting &&
      dayjs(event.start).isSame(dayjs(), 'day'));

  const contentTitleColor = useMemo(() => {
    if (isAiSuggetedEvent) {
      return blackColor;
    }
    if (['energyBoost', 'aiScheduler', 'activity'].includes(event.scheduleFrom!)) {
      return primaryColor;
    }
    return accentColor;
  }, [accentColor, event.scheduleFrom, isAiSuggetedEvent]);

  const renderCategory = () => {
    if (!event.categories[0]) {
      return;
    }
    return (
      <div className="event-block__row-wrapper">
        {event?.categories[0]?.id && (
          <div
            className="event-block__label-group__icon-wrapper"
            style={{ backgroundColor: contentTitleColor }}
          >
            {getDefaultCategoryEventIconSet(event.categories[0].id as DefaultEventCategoryId, 10)}
          </div>
        )}
      </div>
    );
  };

  const renderTaskCheckbox = () => {
    if (monthView || !event.taskFrom || !taskLinkedToEvent || isReplanPreview) {
      return null;
    }
    const isTemporalAIEvent = isTemporalAutoScheduledEvent(event.eventId, ['aiScheduler']);

    return (
      <div className="event-block__checkbox">
        <TaskCheckBox
          isCompleted={taskLinkedToEvent.completed}
          onChange={(checked) => {
            if (!isTemporalAIEvent) {
              if (
                taskLinkedToEvent.appFrom === 'apple-reminders' &&
                !isAppleRemindersDesktopSupported()
              ) {
                return;
              }
              onTaskStatusChange?.(
                event.taskFrom.taskId,
                event.taskFrom.boardId,
                checked,
                taskLinkedToEvent.originalTaskId
              );
            }
          }}
          size={14}
          svgRatio={1}
          color={getCssVariable('--color-primary')}
          disabled={
            (taskLinkedToEvent.appFrom === 'apple-reminders' &&
              !isAppleRemindersDesktopSupported()) ||
            (taskLinkedToEvent.appFrom === 'ticktick' && taskLinkedToEvent.completed)
          }
        />
      </div>
    );
  };

  const renderRoutineCheckbox = () => {
    if (monthView || event.taskFrom || !event.routineFrom || isReplanPreview) {
      return null;
    }

    return (
      <div className="event-block__checkbox">
        <TaskCheckBox
          isCompleted={!!event.routineFrom.completed}
          onChange={(checked) => onRoutineStatusChange?.(event as RBCEvent, checked)}
          size={14}
          color={getCssVariable('--color-primary')}
          svgRatio={1}
        />
      </div>
    );
  };

  const renderTitle = () => (
    <Typography
      variant="subtitle2"
      noWrap
      className={clsx(
        'event-block__content__title',
        isFiveMinuteEvent && 'event-block__content__title--tiny'
      )}
      style={{
        color: event.isAwaitingResponse ? accentColor : blackColor,
        textDecorationLine: event.isDeclined ? 'line-through' : 'none',
      }}
    >
      {monthView && `${dayjs(event.start).format(timeFormat === '12h' ? 'h:mmA' : 'HH:mm')} `}
      {event.title}
    </Typography>
  );

  const renderTapToConfirm = () => {
    if (!showSuggestedIndicator) return null;
    return (
      <div className="event-block__tap-to-confirm">
        <div className="event-block__tap-to-confirm__indicator" />
      </div>
    );
  };

  const renderEventMetrics = () => {
    if (!event.eventMetrics) {
      return null;
    }

    return (
      <EventMetrics
        heartRate={event.eventMetrics.heartRate}
        stress={event.eventMetrics.stress}
        size="sm"
      />
    );
  };

  const renderTime = () => {
    if (!['sleep', 'meal'].includes(event.autoScheduleFrom?.from!) && !isShortEvent) return;
    const isPredicted = event.autoScheduleFrom?.predicted;
    return (
      <Stack
        px={0.5}
        py={0.25}
        className="event-block__content__long-event-time"
        style={event.isAwaitingResponse ? { backgroundColor: 'transparent' } : undefined}
        borderRadius={1}
        mt={0.5}
      >
        {isPredicted && (
          <img
            src={SparkleIcon}
            alt={t('calendar.common.aiSuggestion')}
            className="event-block__content__long-event-time__sparkle"
          />
        )}
        <Typography
          variant="caption"
          className="event-block__content__long-event-time__text"
          style={event.isAwaitingResponse ? { color: accentColor } : undefined}
        >
          {dayjs(event.start).format(timeFormat === '12h' ? 'h:mmA' : 'HH:mm')} -{' '}
          {dayjs(event.end).format(timeFormat === '12h' ? 'h:mmA' : 'HH:mm')}
        </Typography>
      </Stack>
    );
  };

  const renderEditButton = () => {
    const showEditSleepEvent =
      event.autoScheduleFrom?.from === 'sleep' && !isFutureDate(event.end.toISOString());

    if (!showEditSleepEvent) {
      return;
    }

    const isPredicted = event.autoScheduleFrom?.predicted;

    return (
      <Button variant="contained" color="primary" className="event-block__content__edit-button">
        <BorderColor className="event-block__content__edit-button__icon" />
        {isPredicted ? t('calendar.common.add') : t('calendar.common.edit')}
      </Button>
    );
  };

  const renderUpperContent = () => {
    if (isAiSuggetedEvent) {
      return null;
    }

    if (!event.taskFrom && !event.categories?.length) {
      return null;
    }

    return (
      <div className="event-block__label-group">
        {!!event.categories?.length && renderCategory()}
      </div>
    );
  };

  const tintedBg =
    !!event.title && !event.isAwaitingResponse && !showSuggestedIndicator
      ? { background: `${accentColor}33` }
      : undefined;

  return (
    <div
      className={clsx(
        'event-block',
        className,
        !!event.title && 'event-block--with-title',
        event.isAwaitingResponse && 'event-block--awaiting-response',
        showSuggestedIndicator && 'event-block--suggested-task'
      )}
      style={tintedBg}
    >
      <EventImageBgWrapper
        showBg={isAiSuggetedEvent}
        type={event.autoScheduleFrom?.from}
        className={clsx('event-block', 'event-block__image-bg-child')}
        color={event.color || event.calendarColor || primaryColor}
      >
        {isAiSuggetedTemporalTaskEvent && (
          <SuggestedTaskActions
            event={event}
            onDiscard={onDiscardSuggestedTask}
            onAccept={onAcceptSuggestedTask}
            className={clsx(isShortestEvent && 'event-block__suggested-task-actions--shortest')}
          />
        )}
        {event.eventId && !monthView && (
          <div className="event-block__calendar" style={{ backgroundColor: accentColor }} />
        )}
        <div
          className={clsx('event-block__content', isShortEvent && 'event-block__content--short')}
        >
          {isShortEvent ? (
            <>
              {renderTaskCheckbox()}
              {renderRoutineCheckbox()}
              {renderUpperContent()}
              <Stack flex={1} minWidth={0}>
                {renderTitle()}
              </Stack>
            </>
          ) : (
            <>
              {renderUpperContent()}
              <div className="event-block__content__long-event-title-wrapper">
                {renderTaskCheckbox()}
                {renderRoutineCheckbox()}
                <Stack>
                  {renderTitle()}
                  {renderEventMetrics()}
                  {renderTime()}
                  {renderEditButton()}
                </Stack>
              </div>
            </>
          )}
          {renderTapToConfirm()}
        </div>
      </EventImageBgWrapper>
    </div>
  );
};

export default EventBlock;
